import { JSX } from "solid-js";
import { useTelescopeContext } from "@hubble/telescope";
import { openModalV2, goBack } from "~/shared_states/modal";
import { Faqs } from "~/components/brand_l2/faqs";
import { faqs } from "~/components/about_gift_card";
import GradientText from "~/widgets/gradient_text";
import { Accordion } from "~/components/webstore_telescope/accordian_faq_section";

/**
 * Hero Section for Home Webstore Telescope
 * Includes main title and CTA buttons
 */
export function HeroSection(): JSX.Element {
  const { components } = useTelescopeContext();

  const scrollToBrands = () => {
    const brandsSection = document.getElementById("brands-section");
    if (brandsSection) {
      brandsSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const openAboutGiftCardsModal = () => {
    openModalV2(() => <AboutGiftCardsModal />);
  };

  return (
    <div class="flex w-full max-w-[1024px] flex-col items-center gap-8 px-4 sm:px-0">
      {/* Main Title */}
      <div class="flex flex-col items-center gap-1 text-title-2-medium sm:gap-3 sm:text-display-4-medium">
        {/* First Line - Green */}
        <h1 class="text-center  text-success-base ">Enjoy instant discount</h1>

        {/* Second Line - with icon and gradient */}
        <div class="flex flex-wrap items-center justify-center gap-1 text-center  text-text-normal-primary md:gap-[5px]">
          <span>on 400+ brands with</span>
          <div class="flex items-center gap-[6px]">
            {/* Gift box icon */}
            <div class="h-[32px] w-[40px] md:h-[44px] md:w-[56px]">
              <GiftIcon />
            </div>
            <GradientText gradient="linear-gradient(90deg, #8248B0 0%, #0F4788 100%)">
              Gift cards
            </GradientText>
          </div>
        </div>
      </div>

      {/* CTA Buttons */}
      <div class="flex w-full flex-row items-center gap-4 sm:w-[328px]">
        <components.Button
          text="Explore brands"
          variant="primary"
          style="neutral"
          size={48}
          onClick={scrollToBrands}
          expanded={true}
        />
        <components.Button
          text="What's a gift card?"
          variant="secondary"
          style="neutral"
          size={48}
          onClick={openAboutGiftCardsModal}
          expanded={true}
        />
      </div>
    </div>
  );
}

/**
 * About Gift Cards Modal
 * Explains what gift cards are and how to use them
 */
function AboutGiftCardsModal(): JSX.Element {
  const { components } = useTelescopeContext();

  return (
    <div class="flex w-full flex-col gap-4 bg-background-normal-primary ">
      <components.ModalHeader
        title="Learn about gift cards"
        size="medium"
        onClose={() => goBack()}
      />
      <div class="mt-2 px-5 pb-5">
        <Accordion title={null} faqs={faqs} />
      </div>
    </div>
  );
}

const GiftIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 56 44" fill="none">
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M48.5838 39.486H11.9162C8.89108 39.486 6.41602 36.8887 6.41602 33.7142V10.6272C6.41602 7.45276 8.89108 4.85547 11.9162 4.85547H48.5838C51.6088 4.85547 54.0839 7.45276 54.0839 10.6272V33.7142C54.0839 36.8887 51.6088 39.486 48.5838 39.486Z"
        fill="url(#paint0_linear_725_18375)"
      />
      <path
        d="M37.2501 22.6923C40.2649 22.6923 42.7089 19.6977 42.7089 16.0039L42.7144 16.024C42.7144 19.7178 45.1584 22.7123 48.1732 22.7123"
        stroke="white"
        stroke-width="1.83333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M42.7136 16.1068C42.7136 12.7368 44.9433 10.0059 47.6932 10.0059C48.877 10.0059 49.8369 11.0131 49.8369 12.2554C49.8369 14.9691 46.646 17.1699 42.7109 17.1699C38.7759 17.1699 35.585 14.9706 35.585 12.2554C35.585 11.0131 36.5448 10.0059 37.73 10.0059C40.4799 10.0059 42.7096 12.7368 42.7096 16.1068"
        stroke="white"
        stroke-width="1.83333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M43 4V41"
        stroke="white"
        stroke-width="1.83333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M55 17L6 17"
        stroke="white"
        stroke-width="1.83333"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <circle cx="13.5" cy="32.5" r="2.5" fill="white" />
      <circle cx="21.5" cy="32.5" r="2.5" fill="white" />
      <circle cx="29.5" cy="32.5" r="2.5" fill="white" />
      <defs>
        <linearGradient
          id="paint0_linear_725_18375"
          x1="6"
          y1="6.50038"
          x2="262.5"
          y2="33.5004"
          gradientUnits="userSpaceOnUse"
        >
          <stop stop-color="#8248B0" />
          <stop offset="1" stop-color="#0F4788" />
        </linearGradient>
      </defs>
    </svg>
  );
};
